import React from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { MachhaCharacter } from '../lib/machhaAsset';
import { PageHeader } from '../components/UI/PageHeader';
import { usePageTitle } from '../hooks/usePageTitle';
import { useInsights } from '../hooks/useInsights';
import { InsightsSkeleton } from '../components/Insights/InsightsSkeleton';
import { InsightsSummaryRow } from '../components/Insights/InsightsSummaryRow';
import { QuotaGroup } from '../components/Insights/QuotaGroup';
import { UploadHealthGroup } from '../components/Insights/UploadHealthGroup';
import { ProcessingPipelineGroup } from '../components/Insights/ProcessingPipelineGroup';
import { GuestEngagementGroup } from '../components/Insights/GuestEngagementGroup';
import { FaceRecognitionGroup } from '../components/Insights/FaceRecognitionGroup';
import { ContentLibraryGroup } from '../components/Insights/ContentLibraryGroup';
import { TeamCollaborationGroup } from '../components/Insights/TeamCollaborationGroup';
import styles from './InsightsPage.module.css';

function timeAgo(date: Date): string {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
}

export function InsightsPage() {
  usePageTitle('Insights');
  const { data, isLoading, error, refetch, lastFetchedAt } = useInsights();

  const isEmpty = data && data.quota.events.used === 0;

  return (
    <div className={styles['page-container']}>
      <div className={styles['page-top']}>
        <PageHeader
          title="Insights"
          subtitle="Track how your events and albums perform over time"
          className={styles['header-spacing']}
        />
        <div className={styles['refresh-row']}>
          {lastFetchedAt && !isLoading && (
            <span className={styles['last-updated']}>Updated {timeAgo(lastFetchedAt)}</span>
          )}
          <button
            className={styles['refresh-btn']}
            onClick={refetch}
            disabled={isLoading}
            aria-label="Refresh insights"
          >
            <RefreshCw size={14} className={isLoading ? styles['spin'] : ''} />
            Refresh
          </button>
        </div>
      </div>

      {isLoading && <InsightsSkeleton />}

      {error && !isLoading && (
        <div className={styles['error-card']}>
          <AlertCircle size={20} className={styles['error-icon']} />
          <div>
            <div className={styles['error-title']}>Could not load insights</div>
            <div className={styles['error-sub']}>{error}</div>
          </div>
          <button className={styles['retry-btn']} onClick={refetch}>Try again</button>
        </div>
      )}

      {data && isEmpty && (
        <div className={styles['empty-state']}>
          <MachhaCharacter state="Empty" size={160} />
          <h2 className={styles['empty-title']}>No data yet</h2>
          <p className={styles['empty-desc']}>
            Create your first event and upload photos — your insights will appear here.
          </p>
        </div>
      )}

      {data && !isEmpty && (
        <div className={styles['content']}>
          <InsightsSummaryRow data={data.summary} />
          <div className={styles['groups']}>
            <QuotaGroup data={data.quota} />
            <UploadHealthGroup data={data.uploads} />
            <ProcessingPipelineGroup data={data.processing} />
            <GuestEngagementGroup data={data.engagement} />
            <FaceRecognitionGroup data={data.faceRecognition} />
            <ContentLibraryGroup data={data.content} />
            <TeamCollaborationGroup data={data.team} />
          </div>
        </div>
      )}
    </div>
  );
}
